import PropTypes from 'prop-types';
import {useEffect} from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
// hooks
import {useResponsive} from 'packages/common/hooks/useResponsive';
import {useAdminBannerOffset} from 'packages/common/hooks/useAdminBannerOffset';
// components
import Logo from 'packages/common/components/logo';
import Scrollbar from 'packages/common/components/scrollbar';
import {usePathname} from 'packages/common/routes/hooks';
import {NavSectionVertical} from 'packages/common/components/nav-section';
//
import {NAV, SAFE_AREA_X, SAFE_AREA_TOP} from '../config-layout';
import {useNavData} from './config-navigation';
import {NavToggleButton} from '../_common';
import NavFooterSection from './nav-footer-section';

// ----------------------------------------------------------------------

export default function NavVertical({openNav, onCloseNav, onOpenNav}) {
  const adminBannerOffset = useAdminBannerOffset();

  const pathname = usePathname();

  const lgUp = useResponsive('up', 'lg');

  const navData = useNavData();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Stack
      sx={{
        height: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Logo en haut - fixe */}
      <Logo variant="white" sx={{mt: 1.5, ml: 4, mb: 1}} />

      {/* Menu de navigation - zone scrollable */}
      <Scrollbar
        sx={{
          'flexGrow': 1,
          '& .simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <NavSectionVertical
          data={navData}
        />
      </Scrollbar>

      {/* Footer en bas - fixe */}
      <NavFooterSection />
    </Stack>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: {lg: 0},
        width: {lg: NAV.W_VERTICAL},
      }}
    >
      <NavToggleButton />

      {lgUp ? (
        <Stack
          sx={{
            height: adminBannerOffset ? `calc(100vh - ${adminBannerOffset}px)` : '100vh',
            position: 'fixed',
            top: adminBannerOffset,
            width: NAV.W_VERTICAL,
            bgcolor: (theme) => theme.palette.navy[900],
            borderRight: (theme) => `solid 1px rgba(255, 255, 255, 0.08)`,
            // Barre d'état iOS en PWA : le fond navy continue de la remplir, le
            // contenu du menu descend en dessous (0 partout ailleurs).
            paddingTop: SAFE_AREA_TOP,
            boxSizing: 'border-box',
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              // Le tiroir est ancre au bord gauche : en paysage l'encoche mord
              // dessus, on elargit d'autant pour conserver la largeur utile.
              width: `calc(${NAV.W_VERTICAL}px + ${SAFE_AREA_X.LEFT})`,
              bgcolor: (theme) => `${theme.palette.navy[900]} !important`,
              borderRight: (theme) => `solid 1px rgba(255, 255, 255, 0.08)`,
              backgroundImage: 'none',
              // Respecter les zones de securite iOS (encoche, Dynamic Island, barre home)
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              paddingLeft: SAFE_AREA_X.LEFT,
              boxSizing: 'border-box',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  onCloseNav: PropTypes.func,
  onOpenNav: PropTypes.func,
  openNav: PropTypes.bool,
};
